import { formatRegionDisplayName } from '@/utils/stateDisplay'

const SpecialsHelper = {}

SpecialsHelper.getOriginalUnitRrp = (price = {}) =>
  price.regionUnitRrp || price.rrp || null

SpecialsHelper.getRegionalPriceMapFromFamilyProducts = (
  familyProducts = []
) => {
  const regionalPriceMap = {}
  const priceMaps = familyProducts
    .filter((product) => product?.regionalPriceMap)
    .map((product) => product.regionalPriceMap)
  for (const pm of priceMaps) {
    const zoneCodes = Object.keys(pm)
    for (const zc of zoneCodes) {
      regionalPriceMap[zc] = pm[zc]
    }
  }
  return regionalPriceMap
}

SpecialsHelper.transformToRegionalPriceObject = (
  product,
  pricingRegions,
  zoneCode,
  zoneValue,
  tenantCountryRegion = 'Australia'
) => ({
  code: zoneCode,
  cartonPrice: zoneValue[0].price,
  amountOff: null,
  unitRrp: null,
  originalUnitRrp: SpecialsHelper.getOriginalUnitRrp(zoneValue[0]),
  grossProfitPercentage: null,
  displayName: formatRegionDisplayName(
    pricingRegions.find((r) => r.code === zoneCode),
    tenantCountryRegion
  ),
  hasGst: product.hasGst,
  hasWet: product.hasWet,
  packageQuantity: product.packageQuantity,
  // only gets added for variable weight products
  variableWeightTargetInKg: product.variableWeightTargetInKg
})

SpecialsHelper.transformProductsToRegionalPriceMap = (
  products = [],
  pricingRegions = [],
  tenantCountryRegion = 'Australia'
) => {
  const pricedProducts = products.filter((product) => product?.regionalPriceMap)
  if (!pricedProducts.length) return []

  const regionalPriceMap =
    SpecialsHelper.getRegionalPriceMapFromFamilyProducts(pricedProducts)
  const lowestPackageQuantityProductMin = Math.min.apply(
    null,
    pricedProducts.map((p) => p.packageQuantity)
  )
  const lowestPackageQuantityProduct = pricedProducts.find(
    (p) => p.packageQuantity === lowestPackageQuantityProductMin
  )
  return Object.entries(regionalPriceMap).map(([zoneCode, zoneValues]) =>
    SpecialsHelper.transformToRegionalPriceObject(
      lowestPackageQuantityProduct,
      pricingRegions,
      zoneCode,
      zoneValues,
      tenantCountryRegion
    )
  )
}

SpecialsHelper.transformProductToRegionalPriceMap = (
  product,
  pricingRegions,
  tenantCountryRegion = 'Australia'
) =>
  SpecialsHelper.transformProductsToRegionalPriceMap(
    [product],
    pricingRegions,
    tenantCountryRegion
  )

SpecialsHelper.transformFamilyGroupZonePricesToRegionalPriceMap = (
  familyGroup = {},
  pricingRegions = [],
  tenantCountryRegion = 'Australia'
) => {
  const zonePrices = familyGroup.zonePrices || []
  if (!zonePrices.length) return []

  return zonePrices.map((zone) => ({
    code: zone.pricingRegionCode,
    cartonPrice: zone.priceExGst,
    amountOff: null,
    unitRrp: null,
    originalUnitRrp: SpecialsHelper.getOriginalUnitRrp(zone),
    grossProfitPercentage: null,
    displayName: formatRegionDisplayName(
      pricingRegions.find((region) => region.code === zone.pricingRegionCode),
      tenantCountryRegion
    ),
    hasGst: familyGroup.hasGst,
    hasWet: familyGroup.hasWet,
    packageQuantity: familyGroup.packageQuantity,
    variableWeightTargetInKg: familyGroup.variableWeightTargetInKg
  }))
}

// ***
// Helpers related to validations
// ***

SpecialsHelper.hasNoPricedZones = (
  regionalPriceMap = [],
  isUsTenant = false
) => {
  if (isUsTenant) {
    return !regionalPriceMap.find(
      (p) =>
        p.amountOff > 0 &&
        (p.revisedUnitPriceExTax ?? p.revisedCostPerKGExTax ?? 0) > 0
    )
  }
  return !regionalPriceMap.find(
    (p) => p.amountOff > 0 && p.unitRrp > 0 && p.grossProfitPercentage > 0
  )
}

SpecialsHelper.hasIncompletePricing = (regionalPriceMap = []) => {
  // Find rows that have one of its fields filled
  const validatableRegionPriceMapRows = regionalPriceMap?.filter(
    (row) => row.amountOff || row.unitRrp
  )

  // Find rows that are fully filled
  const completedRegionPriceMapRows = validatableRegionPriceMapRows.filter(
    (row) => row.amountOff && row.unitRrp
  )

  // Cannot have uncompleted row fields
  return (
    validatableRegionPriceMapRows.length !== completedRegionPriceMapRows.length
  )
}

SpecialsHelper.hasInvalidPricing = (
  regionalPriceMap = [],
  isUsTenant = false
) => {
  if (isUsTenant) {
    return !!regionalPriceMap?.find(
      (zone) => zone.amountOff < 0 || zone.amountOff >= zone.cartonPrice
    )
  }
  return !!regionalPriceMap?.find(
    (zone) =>
      zone.amountOff < 0 ||
      zone.amountOff >= zone.cartonPrice ||
      zone.unitRrp < 0 ||
      zone.grossProfitPercentage < 0 ||
      (zone.amountOff > 0 && !(zone.unitRrp > 0)) ||
      (!(zone.amountOff > 0) && zone.unitRrp > 0)
  )
}

SpecialsHelper.hasValidPricing = (
  regionalPriceMap = [],
  isUsTenant = false
) => {
  if (isUsTenant) {
    return !!regionalPriceMap?.find(
      (zone) =>
        zone.cartonPrice > zone.amountOff &&
        zone.amountOff > 0 &&
        (zone.revisedUnitPriceExTax ?? zone.revisedCostPerKGExTax ?? 0) > 0
    )
  }
  return !!regionalPriceMap?.find(
    (zone) =>
      zone.cartonPrice > zone.amountOff &&
      zone.amountOff > 0 &&
      zone.unitRrp > 0 &&
      zone.grossProfitPercentage >= 0
  )
}

export default SpecialsHelper
